import { observer } from 'mobx-react-lite'
import { useEffect, useState, useCallback } from 'react'
import { useHistory, useParams, Link, Prompt } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import {
  Container,
  Paper,
  Title,
  TextInput,
  Textarea,
  Button,
  Group,
  Avatar,
  Box,
  Text,
  Switch
} from '@mantine/core'
import { FiUpload } from 'react-icons/fi'
import Breadcrumb from '../../app/common/Breadcrumb'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { useStore } from '../../app/stores/store'
import { toast } from 'react-toastify'
import PhotoUploadCropModal from './PhotoUploadCropModal'

const validationSchema = Yup.object({
  displayName: Yup.string().required('Display name is required').max(200, 'Max 200 characters'),
  bio: Yup.string().max(500, 'Max 500 characters'),
  coverImageUrl: Yup.string()
    .max(2000, 'Max 2000 characters')
    .test('url', 'Must be a valid URL', (value) => !value || value.trim() === '' || /^https?:\/\/.+/i.test(value.trim()))
})

export default observer(function ProfileEditForm () {
  const history = useHistory()
  const { username } = useParams<{ username: string }>()
  const { profileStore, userStore } = useStore()
  const { profile, loadProfile, updateProfile, uploadPhoto, loadingProfile, uploading } = profileStore
  const [avatarFile, setAvatarFile] = useState<File | null>(null)

  useEffect(() => {
    if (username) loadProfile(username)
  }, [username, loadProfile])

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) setAvatarFile(accepted[0])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg,image/jpg,image/png,image/webp,image/gif',
    maxFiles: 1,
    multiple: false,
    maxSize: 5 * 1024 * 1024,
    onDropRejected: (fileRejections) => {
      const err = fileRejections[0]?.errors[0]
      if (err?.code === 'file-too-large') toast.error('Image must be under 5MB')
      else toast.error(err?.message || 'Invalid image')
    }
  })

  const handleAvatarCrop = async (blob: Blob) => {
    await uploadPhoto(blob, true)
    setAvatarFile(null)
  }

  if (!userStore.isLoggedIn) {
    history.push('/login')
    return null
  }

  if (loadingProfile || !profile) {
    return <LoadingComponent content="Loading profile..." />
  }

  if (profile.username !== username) {
    return <LoadingComponent content="Loading profile..." />
  }

  if (!profileStore.canEdit) {
    toast.error('You cannot edit this profile')
    history.push(`/profiles/${username}`)
    return null
  }

  const breadcrumbItems = [
    { label: 'Profile', href: `/profiles/${username}` },
    { label: 'Edit' }
  ]

  return (
    <Container size="md" py="xl">
      <Breadcrumb items={breadcrumbItems} />
      <Group position="apart" mb="lg">
        <Title order={3}>Edit profile</Title>
        <Button component={Link} to={`/profiles/${username}`} variant="subtle">
          Cancel
        </Button>
      </Group>

      <Paper p="lg" radius="md" withBorder>
        <Box mb="xl">
          <Text size="sm" weight={500} mb="xs">
            Profile photo
          </Text>
          <Group spacing="md">
            <Avatar src={profile.image || '/assets/user.png'} alt="" radius="xl" size={100} />
            <Box
              {...getRootProps()}
              sx={{
                padding: 12,
                border: '2px dashed var(--mantine-color-gray-4)',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'var(--mantine-color-orange-0)' : undefined,
                transition: 'background-color 0.15s ease'
              }}
            >
              <input {...getInputProps()} />
              <Group spacing="xs">
                <FiUpload size={20} />
                <Text size="sm" color="dimmed">
                  Drop image or click to change
                </Text>
              </Group>
            </Box>
          </Group>
        </Box>

        <Formik
          validationSchema={validationSchema}
          enableReinitialize
          initialValues={{
            displayName: profile.displayName,
            bio: profile.bio ?? '',
            coverImageUrl: profile.coverImageUrl ?? '',
            isVaultPublic: profile.isVaultPublic !== false,
            isWishlistPublic: profile.isWishlistPublic !== false
          }}
          onSubmit={async (values, { resetForm }) => {
            await updateProfile(
              values.displayName,
              values.bio || undefined,
              values.coverImageUrl?.trim() || null,
              values.isVaultPublic,
              values.isWishlistPublic
            )
            toast.success('Profile updated')
            resetForm({ values })
            history.push(`/profiles/${username}`)
          }}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty, values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
            <>
              <Prompt when={dirty} message="You have unsaved changes. Leave anyway?" />
              <Form onSubmit={handleSubmit}>
              <TextInput
                label="Display name"
                name="displayName"
                value={values.displayName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.displayName && errors.displayName}
                placeholder="Your display name"
                required
                mb="md"
              />
              <Textarea
                label="Bio"
                name="bio"
                value={values.bio}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bio && errors.bio}
                placeholder="Tell us about yourself (optional)"
                minRows={4}
                mb="md"
              />
              <Box mb="md">
                <Switch
                  label="Public vault"
                  description="Allow others to see your Gun Vault from your profile."
                  checked={values.isVaultPublic}
                  onChange={(e) => setFieldValue('isVaultPublic', e.currentTarget.checked)}
                />
              </Box>
              <Box mb="md">
                <Switch
                  label="Public wishlist"
                  description="When off, your wishlist is only visible to you. Individual items can still be hidden below."
                  checked={values.isWishlistPublic}
                  onChange={(e) => setFieldValue('isWishlistPublic', e.currentTarget.checked)}
                />
              </Box>
              <TextInput
                label="Cover image URL"
                name="coverImageUrl"
                value={values.coverImageUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.coverImageUrl && errors.coverImageUrl}
                placeholder="https://…"
                description="Optional. Paste an image URL to use as your profile cover. Leave blank to keep current or clear."
                mb="lg"
              />
              <Button type="submit" disabled={isSubmitting || !dirty || !isValid} loading={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save changes'}
              </Button>
            </Form>
            </>
          )}
        </Formik>
      </Paper>

      <PhotoUploadCropModal
        file={avatarFile}
        open={!!avatarFile}
        onClose={() => setAvatarFile(null)}
        onCrop={handleAvatarCrop}
        loading={uploading}
      />
    </Container>
  )
})
